import { useState } from 'react';

import { useApiClient } from '@eluve/api-client-provider';
import { useAppointmentId } from '@eluve/frontend-appointment-hooks';
import { useNamedLogger } from '@eluve/logger';
import { useTenantIdFromParams } from '@eluve/session-helpers';
import { useAppointmentTemplates } from '@eluve/smart-blocks';

interface SelectedTemplates {
  initialPromptTemplateId: string;
  initialOutputTemplateId: string | null;
}

/**
 * Shared hook for regenerating appointment summaries.
 * Handles template updates (if changed) and triggering the summarize endpoint.
 *
 * Components are responsible for:
 * - Determining when regeneration should be disabled (UI state)
 * - Success/error handling and user feedback (toasts, logs)
 * - Analytics tracking
 */
export const useRegenerateSummary = () => {
  const logger = useNamedLogger('useRegenerateSummary');
  const tenantId = useTenantIdFromParams();
  const appointmentId = useAppointmentId();
  const apiClient = useApiClient();

  const {
    initialPromptTemplateId,
    initialLlmOutputTemplateId,
    updateAppointmentTemplates,
  } = useAppointmentTemplates();

  const [selectedTemplates, setSelectedTemplates] =
    useState<SelectedTemplates | null>(null);
  const [isRegenerating, setIsRegenerating] = useState(false);

  const regenerateSummary = async (
    onSuccess?: () => void,
    onError?: (error: Error) => void,
  ) => {
    setIsRegenerating(true);
    try {
      // Update templates if changed
      if (selectedTemplates) {
        await updateAppointmentTemplates({
          variables: {
            tenantId: tenantId!,
            id: appointmentId,
            initialPromptTemplateId: selectedTemplates.initialPromptTemplateId,
            initialLlmOutputTemplateId:
              selectedTemplates.initialOutputTemplateId,
          },
        });
      }

      // Trigger summarization
      const result = await apiClient.llm.summarizeAppointment({
        body: {},
        params: {
          tenantId: tenantId!,
          appointmentId,
        },
      });

      if (result.status !== 201) {
        throw new Error('Failed to start summarization job');
      }

      logger.info('Successfully triggered note regeneration', {
        appointmentId,
      });
      onSuccess?.();
    } catch (error) {
      logger.error('Failed to regenerate summary', {
        appointmentId,
        error,
      });
      onError?.(error as Error);
    } finally {
      setIsRegenerating(false);
    }
  };

  return {
    selectedTemplates,
    setSelectedTemplates,
    initialPromptTemplateId,
    initialLlmOutputTemplateId,
    regenerateSummary,
    isRegenerating,
  };
};
